import JSZip from 'jszip';
import { LoyaltyCampaignConfigRow } from './types';
import { getSkuPriceUSD, parseSkus } from './utils';

type WorksheetDescriptor = {
  name: string;
  target: string;
};

type ParsedWorksheet = {
  headers: Record<string, string>;
  rows: Array<Record<string, string>>;
};

type ParseWorkbookResult = {
  configs: LoyaltyCampaignConfigRow[];
  sheetName: string;
  warnings: string[];
};

type ExportWorksheetRow = {
  price: number | null;
  amount: number;
  iosSkus: string[];
  androidSkus: string[];
  otherSkus: string[];
};

const SPREADSHEET_NAMESPACE = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const OFFICE_RELATIONSHIP_NAMESPACE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const PACKAGE_RELATIONSHIP_NAMESPACE = 'http://schemas.openxmlformats.org/package/2006/relationships';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_XML_CHARS = 8 * 1024 * 1024; // per XML part
const MAX_SHARED_STRINGS = 50_000;
const MAX_SHEETS = 50;
const MAX_ROWS = 20_000;
const MAX_CELLS_PER_ROW = 200;

const normalizeHeader = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\(\$\)/g, '')
    .replace(/\*/g, '');

const splitCellRef = (ref: string): { col: string; row: number } | null => {
  const match = /^([A-Z]+)(\d+)$/.exec(ref);
  if (!match) {
    return null;
  }
  return { col: match[1], row: Number(match[2]) };
};

const assertSafeInputFile = (file: File): void => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    throw new Error('Only .xlsx files are supported.');
  }
  if (file.size <= 0) {
    throw new Error('Workbook is empty.');
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`Workbook is too large. Maximum allowed size is ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB.`);
  }
};

const assertSafeXmlString = (xml: string, partName: string): void => {
  if (!xml) {
    throw new Error(`${partName} is empty.`);
  }
  if (xml.length > MAX_XML_CHARS) {
    throw new Error(`${partName} is too large to process safely.`);
  }
};

const parseXmlDocument = (xml: string, partName: string): Document => {
  assertSafeXmlString(xml, partName);
  const doc = new DOMParser().parseFromString(xml, 'application/xml');

  // Browser DOMParser returns parsererror nodes on invalid XML
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error(`Invalid XML in ${partName}.`);
  }

  return doc;
};

const getTextContent = (node: Element | null): string => {
  if (!node) {
    return '';
  }
  return Array.from(node.getElementsByTagNameNS(SPREADSHEET_NAMESPACE, 't'))
    .map((textNode) => textNode.textContent ?? '')
    .join('');
};

const getCellValue = (cell: Element, sharedStrings: string[]): string => {
  const cellType = cell.getAttribute('t');
  if (cellType === 'inlineStr') {
    return getTextContent(cell.getElementsByTagNameNS(SPREADSHEET_NAMESPACE, 'is')[0] ?? null);
  }

  const valueNode = cell.getElementsByTagNameNS(SPREADSHEET_NAMESPACE, 'v')[0];
  const rawValue = valueNode?.textContent ?? '';

  if (cellType === 's') {
    const sharedIndex = Number(rawValue);
    return Number.isFinite(sharedIndex) ? sharedStrings[sharedIndex] ?? '' : '';
  }

  return rawValue;
};

const getSharedStrings = (doc: Document): string[] => {
  const items = Array.from(doc.getElementsByTagNameNS(SPREADSHEET_NAMESPACE, 'si'));
  if (items.length > MAX_SHARED_STRINGS) {
    throw new Error('Workbook has too many shared strings.');
  }
  return items.map((si) => getTextContent(si));
};

const normalizeZipTargetToWorkbookPath = (target: string): string | null => {
  const trimmed = target.trim().replace(/\\/g, '/');
  if (!trimmed) {
    return null;
  }

  // Reject absolute paths, traversal, and suspicious paths
  if (
    trimmed.startsWith('/') ||
    trimmed.includes('../') ||
    trimmed.includes('..\\') ||
    trimmed.includes('://')
  ) {
    return null;
  }

  return trimmed.startsWith('xl/') ? trimmed : `xl/${trimmed}`;
};

const getWorkbookSheets = (workbookDoc: Document, relsDoc: Document): WorksheetDescriptor[] => {
  const relMap = new Map<string, string>();

  Array.from(relsDoc.getElementsByTagNameNS(PACKAGE_RELATIONSHIP_NAMESPACE, 'Relationship')).forEach((relationship) => {
    const id = relationship.getAttribute('Id');
    const target = relationship.getAttribute('Target');
    const type = relationship.getAttribute('Type') ?? '';

    // Only accept worksheet relationships
    if (!type.endsWith('/worksheet')) {
      return;
    }

    const normalizedTarget = target ? normalizeZipTargetToWorkbookPath(target) : null;
    if (id && normalizedTarget) {
      relMap.set(id, normalizedTarget);
    }
  });

  const sheets = Array.from(workbookDoc.getElementsByTagNameNS(SPREADSHEET_NAMESPACE, 'sheet'))
    .map((sheet) => {
      const name = sheet.getAttribute('name');
      const relationshipId = sheet.getAttributeNS(OFFICE_RELATIONSHIP_NAMESPACE, 'id');
      const target = relationshipId ? relMap.get(relationshipId) : null;
      if (!name || !target) {
        return null;
      }
      return { name, target };
    })
    .filter((sheet): sheet is WorksheetDescriptor => sheet !== null);

  if (sheets.length > MAX_SHEETS) {
    throw new Error('Workbook has too many sheets.');
  }

  return sheets;
};

const parseWorksheet = (worksheetDoc: Document, sharedStrings: string[]): ParsedWorksheet | null => {
  const rows = Array.from(worksheetDoc.getElementsByTagNameNS(SPREADSHEET_NAMESPACE, 'row'));
  if (rows.length === 0) {
    return null;
  }
  if (rows.length > MAX_ROWS) {
    throw new Error('Worksheet has too many rows.');
  }

  const byRow = new Map<number, Record<string, string>>();

  for (const rowNode of rows) {
    const rowIndex = Number(rowNode.getAttribute('r') ?? 0);
    if (Number.isFinite(rowIndex) && rowIndex > 0) {
      const cellNodes = Array.from(rowNode.getElementsByTagNameNS(SPREADSHEET_NAMESPACE, 'c'));
      if (cellNodes.length > MAX_CELLS_PER_ROW) {
        throw new Error(`Row ${rowIndex} has too many cells.`);
      }

      const rowValues: Record<string, string> = {};
      for (const cell of cellNodes) {
        const ref = cell.getAttribute('r');
        const parsedRef = ref ? splitCellRef(ref) : null;
        if (parsedRef) {
          rowValues[parsedRef.col] = getCellValue(cell, sharedStrings).trim();
        }
      }

      byRow.set(rowIndex, rowValues);
    }
  }

  const headerEntry = Array.from(byRow.entries()).find(([, row]) =>
    Object.values(row).some((value) => normalizeHeader(value) === 'total loyalty points')
  );
  if (!headerEntry) {
    return null;
  }

  const [headerRowIndex, rawHeaders] = headerEntry;
  const headers = Object.entries(rawHeaders).reduce<Record<string, string>>((acc, [col, value]) => {
    acc[col] = normalizeHeader(value);
    return acc;
  }, {});

  const dataRows = Array.from(byRow.entries())
    .filter(([rowIndex]) => rowIndex > headerRowIndex)
    .sort((left, right) => left[0] - right[0])
    .map(([, row]) => row);

  return { headers, rows: dataRows };
};

const findHeaderColumn = (headers: Record<string, string>, candidates: string[]): string | null =>
  Object.entries(headers).find(([, value]) => candidates.includes(value))?.[0] ?? null;

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/[$,]/g, '');
  if (!normalized) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSkusForPrice = (price: number, skuPriceMap: Record<string, number>): string[] =>
  Object.entries(skuPriceMap)
    .filter(([, currentPrice]) => Math.abs(currentPrice - price) < 0.0001)
    .map(([sku]) => sku)
    .sort((left, right) => left.localeCompare(right));

const getSkuPlatformGroup = (sku: string): 'ios' | 'android' | 'other' => {
  const normalizedSku = sku.trim().toLowerCase();
  if (normalizedSku.includes('.ios.') || normalizedSku.includes('ios')) {
    return 'ios';
  }
  if (normalizedSku.includes('.android.') || normalizedSku.includes('android')) {
    return 'android';
  }
  return 'other';
};

const xmlEscape = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

// Prevent spreadsheet formula injection when output is later opened in Excel
const sanitizeSpreadsheetText = (value: string): string => {
  const trimmed = value.trimStart();
  return /^[=+\-@]/.test(trimmed) ? `'${value}` : value;
};

const buildInlineStringCell = (ref: string, value: string): string =>
  `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(sanitizeSpreadsheetText(value))}</t></is></c>`;

const buildNumberCell = (ref: string, value: number): string => `<c r="${ref}"><v>${value}</v></c>`;

const buildExportWorksheetRows = (
  configs: LoyaltyCampaignConfigRow[],
  skuPriceMap: Record<string, number>
): ExportWorksheetRow[] => {
  const groupedRows = new Map<string, ExportWorksheetRow>();

  configs.forEach((config) => {
    const price = getSkuPriceUSD(config.id, skuPriceMap);
    const key = `${price === null ? 'noprice' : price.toFixed(2)}::${config.amount}`;
    const existing =
      groupedRows.get(key) ?? {
        price,
        amount: config.amount,
        iosSkus: [],
        androidSkus: [],
        otherSkus: [],
      };
    const platformGroup = getSkuPlatformGroup(config.id);
    if (platformGroup === 'ios' && !existing.iosSkus.includes(config.id)) {
      existing.iosSkus.push(config.id);
    } else if (platformGroup === 'android' && !existing.androidSkus.includes(config.id)) {
      existing.androidSkus.push(config.id);
    } else if (platformGroup === 'other' && !existing.otherSkus.includes(config.id)) {
      existing.otherSkus.push(config.id);
    }
    groupedRows.set(key, existing);
  });

  return Array.from(groupedRows.values())
    .map((row) => ({
      ...row,
      iosSkus: row.iosSkus.sort((left, right) => left.localeCompare(right)),
      androidSkus: row.androidSkus.sort((left, right) => left.localeCompare(right)),
      otherSkus: row.otherSkus.sort((left, right) => left.localeCompare(right)),
    }))
    .sort((left, right) => {
      if (left.price === null && right.price === null) {
        return left.amount - right.amount;
      }
      if (left.price === null) {
        return 1;
      }
      if (right.price === null) {
        return -1;
      }
      return left.price - right.price || left.amount - right.amount;
    });
};

const buildWorksheetXml = (rows: ExportWorksheetRow[]): string => {
  const headerRow = [
    buildInlineStringCell('A1', 'Cost in Shop ($)'),
    buildInlineStringCell('B1', 'Total Loyalty Points'),
    buildInlineStringCell('C1', 'Current iOS SKU(s)'),
    buildInlineStringCell('D1', 'Current Android SKU(s)'),
    buildInlineStringCell('E1', 'Current Other/Subscription SKU(s)'),
  ].join('');

  const dataRows = rows
    .map((row, index) => {
      const rowNumber = index + 2;
      const cells = [
        row.price !== null ? buildNumberCell(`A${rowNumber}`, row.price) : buildInlineStringCell(`A${rowNumber}`, ''),
        buildNumberCell(`B${rowNumber}`, row.amount),
        buildInlineStringCell(`C${rowNumber}`, row.iosSkus.join('\n')),
        buildInlineStringCell(`D${rowNumber}`, row.androidSkus.join('\n')),
        buildInlineStringCell(`E${rowNumber}`, row.otherSkus.join('\n')),
      ].join('');
      return `<row r="${rowNumber}">${cells}</row>`;
    })
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="${SPREADSHEET_NAMESPACE}">
  <sheetData>
    <row r="1">${headerRow}</row>
    ${dataRows}
  </sheetData>
</worksheet>`;
};

const parseWorksheetConfigs = (
  parsedWorksheet: ParsedWorksheet,
  skuPriceMap: Record<string, number>
): { configs: LoyaltyCampaignConfigRow[]; warnings: string[] } | null => {
  const totalPointsColumn = findHeaderColumn(parsedWorksheet.headers, ['total loyalty points', 'loyalty rounded']);
  if (!totalPointsColumn) {
    return null;
  }

  const priceColumn = findHeaderColumn(parsedWorksheet.headers, [
    'cost in shop',
    'cost in shop $',
    'cost in shop, $',
    'prices',
  ]);
  const iosSkuColumn = findHeaderColumn(parsedWorksheet.headers, ['current ios sku(s)']);
  const androidSkuColumn = findHeaderColumn(parsedWorksheet.headers, ['current android sku(s)']);
  const otherSkuColumn = findHeaderColumn(parsedWorksheet.headers, ['current other/subscription sku(s)']);

  const warnings: string[] = [];
  const configMap = new Map<string, LoyaltyCampaignConfigRow>();

  parsedWorksheet.rows.forEach((row) => {
    const amount = parseNumber(row[totalPointsColumn] ?? '');
    const price = priceColumn ? parseNumber(row[priceColumn] ?? '') : null;

    if (amount !== null) {
      const explicitSkuColumns = [
        iosSkuColumn ? row[iosSkuColumn] ?? '' : '',
        androidSkuColumn ? row[androidSkuColumn] ?? '' : '',
        otherSkuColumn ? row[otherSkuColumn] ?? '' : '',
      ];
      const explicitSkus = parseSkus(explicitSkuColumns.filter(Boolean).join('\n'));
      const rowSkus =
        explicitSkus.length > 0 ? explicitSkus : price !== null ? getSkusForPrice(price, skuPriceMap) : [];

      if (rowSkus.length === 0) {
        if (price !== null) {
          warnings.push(`No current SKU found for $${price.toFixed(2)}.`);
        }
        return;
      }

      rowSkus.forEach((sku) => {
        const existing = configMap.get(sku);
        if (existing && existing.amount !== amount) {
          throw new Error(`SKU "${sku}" has conflicting points amounts in workbook.`);
        }
        configMap.set(sku, { id: sku, amount });
      });
    }
  });

  return configMap.size > 0
    ? {
        configs: Array.from(configMap.values()).sort((left, right) => left.id.localeCompare(right.id)),
        warnings,
      }
    : null;
};

export const parseLoyaltyConfigWorkbook = async (
  file: File,
  skuPriceMap: Record<string, number>
): Promise<ParseWorkbookResult> => {
  assertSafeInputFile(file);

  const zip = await JSZip.loadAsync(await file.arrayBuffer(), {
    checkCRC32: true,
  });

  const workbookXml = await zip.file('xl/workbook.xml')?.async('string');
  const relsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');

  if (!workbookXml || !relsXml) {
    throw new Error('Workbook structure is invalid.');
  }

  const workbookDoc = parseXmlDocument(workbookXml, 'xl/workbook.xml');
  const relsDoc = parseXmlDocument(relsXml, 'xl/_rels/workbook.xml.rels');

  const sharedStringsXml = await zip.file('xl/sharedStrings.xml')?.async('string');
  const sharedStrings = sharedStringsXml
    ? getSharedStrings(parseXmlDocument(sharedStringsXml, 'xl/sharedStrings.xml'))
    : [];

  const sheets = getWorkbookSheets(workbookDoc, relsDoc);

  for (const sheet of sheets) {
    const worksheetFile = zip.file(sheet.target);
    if (worksheetFile) {
      const worksheetXml = await worksheetFile.async('string');
      const parsedWorksheet = parseWorksheet(parseXmlDocument(worksheetXml, sheet.target), sharedStrings);
      const parsedConfigs = parsedWorksheet ? parseWorksheetConfigs(parsedWorksheet, skuPriceMap) : null;

      if (parsedConfigs) {
        return {
          configs: parsedConfigs.configs,
          sheetName: sheet.name,
          warnings: parsedConfigs.warnings,
        };
      }
    }
  }

  throw new Error('No Loyalty Point Mapping table was found in this workbook.');
};

export const downloadLoyaltyConfigWorkbook = async (
  configs: LoyaltyCampaignConfigRow[],
  skuPriceMap: Record<string, number>
): Promise<void> => {
  const zip = new JSZip();
  const worksheetRows = buildExportWorksheetRows(configs, skuPriceMap);

  zip.file(
    '[Content_Types].xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`
  );
  zip.file(
    '_rels/.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="${PACKAGE_RELATIONSHIP_NAMESPACE}">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`
  );
  zip.file(
    'xl/workbook.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="${SPREADSHEET_NAMESPACE}" xmlns:r="${OFFICE_RELATIONSHIP_NAMESPACE}">
  <sheets>
    <sheet name="Loyalty Point Mapping" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`
  );
  zip.file(
    'xl/_rels/workbook.xml.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="${PACKAGE_RELATIONSHIP_NAMESPACE}">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`
  );
  zip.file('xl/worksheets/sheet1.xml', buildWorksheetXml(worksheetRows));

  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'LoyaltyPointMapping-current-config.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};